import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import SuperAdminLayout from "../components/SuperAdminLayout";
import api from "../services/api";


export default function SuperAdminDashboardPage() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    fetchStats();
  }, []);


  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await api.get("/super-admin/stats");
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load stats", err);
    } finally {
      setLoading(false);
    }
  };


  if (loading) return (
    <div className="flex items-center justify-center h-full min-h-screen">
      <div className="flex items-center gap-3 text-slate-400">
        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        Loading platform data...
      </div>
    </div>
  );


  const statCards = [
    { label: "Total Shops",    value: stats?.total_shops     ?? 0, icon: "🏪", color: "from-blue-500/20 to-blue-600/10",    text: "text-blue-400" },
    { label: "Active",         value: stats?.active_shops    ?? 0, icon: "✅", color: "from-green-500/20 to-green-600/10",  text: "text-green-400" },
    { label: "On Trial",       value: stats?.trial_shops     ?? 0, icon: "⏳", color: "from-amber-500/20 to-amber-600/10",  text: "text-amber-400" },
    { label: "Expired",        value: stats?.expired_shops   ?? 0, icon: "❌", color: "from-red-500/20 to-red-600/10",      text: "text-red-400" },
    { label: "Suspended",      value: stats?.suspended_shops ?? 0, icon: "🚫", color: "from-slate-500/20 to-slate-600/10",  text: "text-slate-400" },
    { label: "Total Drivers",  value: stats?.total_drivers   ?? 0, icon: "🏍️", color: "from-purple-500/20 to-purple-600/10",text: "text-purple-400" },
    { label: "Total Orders",   value: stats?.total_orders    ?? 0, icon: "📦", color: "from-cyan-500/20 to-cyan-600/10",    text: "text-cyan-400" },
    { label: "Today's Orders", value: stats?.today_orders    ?? 0, icon: "📬", color: "from-orange-500/20 to-orange-600/10",text: "text-orange-400" },
  ];


  const chartData = Object.entries(stats?.monthly_signups ?? {}).map(([month, count]) => ({
    month: month.split(" ")[0], // just "Jan", "Feb" etc
    shops: count,
  }));


  const expiringSoon = stats?.expiring_soon ?? [];
  const churnRisk    = stats?.churn_risk    ?? [];


  const formatKES = (val) => `KES ${Number(val ?? 0).toLocaleString()}`;


  const hoursLabel = (hours) => {
    if (hours < 24) return `${hours}h left`;
    return `${Math.floor(hours / 24)}d left`;
  };


  const openWhatsApp = (phone) => {
    const clean = phone?.replace(/^0/, "254");
    window.open(`https://wa.me/${clean}`, "_blank");
  };


  const openCall = (phone) => {
    window.open(`tel:${phone}`, "_blank");
  };


  return (
    <SuperAdminLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>


      <div className="p-7 space-y-8">


        {/* ── HEADER ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
              Platform Overview
            </h1>
            <p className="text-slate-400 text-sm mt-1">Real-time view across all TumaTrack shops.</p>
          </div>
          <button
            onClick={fetchStats}
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors border border-slate-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Refresh
          </button>
        </div>


        {/* ── EXPIRY ALERT ── */}
        {expiringSoon.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">🚨</span>
              <h2 className="text-red-400 font-bold text-sm uppercase tracking-widest">
                Trials Expiring Soon — {expiringSoon.length} shop{expiringSoon.length > 1 ? "s" : ""}
              </h2>
            </div>
            <div className="space-y-3">
              {expiringSoon.map((shop) => (
                <div key={shop.id} className="flex items-center justify-between bg-slate-900/60 rounded-xl px-4 py-3 gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{shop.name}</p>
                    <p className="text-slate-400 text-xs">{shop.owner?.name} · {shop.owner?.phone}</p>
                  </div>
                  <span className="text-red-400 text-xs font-bold bg-red-500/10 border border-red-500/20 px-2.5 py-1 rounded-full shrink-0">
                    ⏰ {hoursLabel(shop.hours_left)}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openWhatsApp(shop.owner?.phone)}
                      className="bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/20 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                    >
                      💬 WhatsApp
                    </button>
                    <button
                      onClick={() => openCall(shop.owner?.phone)}
                      className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                    >
                      📞 Call
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* ── MRR HIGHLIGHT ── */}
        <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/10 border border-orange-500/20 rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-orange-400 text-sm font-semibold uppercase tracking-widest mb-1">
              Monthly Recurring Revenue
            </p>
            <p className="text-4xl font-extrabold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
              {formatKES(stats?.estimated_mrr)}
            </p>
            <p className="text-slate-400 text-xs mt-1">Based on active paid plans</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/super-admin/payments"
              className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold px-5 py-2.5 rounded-xl transition-all text-sm shrink-0 border border-slate-700"
            >
              💳 View Payments
            </Link>
            <Link
              to="/super-admin/shops"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold px-5 py-2.5 rounded-xl transition-all text-sm shrink-0"
            >
              Manage All Shops
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>


        {/* ── STATS GRID ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((card) => (
            <div key={card.label} className={`bg-gradient-to-br ${card.color} border border-slate-800 rounded-2xl p-5`}>
              <span className="text-xl block mb-3">{card.icon}</span>
              <p className={`text-3xl font-extrabold ${card.text}`} style={{ fontFamily: "'Sora', sans-serif" }}>
                {card.value}
              </p>
              <p className="text-slate-400 text-xs mt-1">{card.label}</p>
            </div>
          ))}
        </div>


        {/* ── CHARTS + CHURN ROW ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


          {/* Monthly Signups Chart */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-white font-bold text-base mb-5" style={{ fontFamily: "'Sora', sans-serif" }}>
              📈 New Shops — Last 6 Months
            </h2>
            {chartData.every((d) => d.shops === 0) ? (
              <div className="flex items-center justify-center h-40 text-slate-500 text-sm">
                No signup data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData} barSize={28}>
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#94a3b8", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#94a3b8", fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    width={24}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "12px",
                      color: "#f1f5f9",
                      fontSize: "12px",
                    }}
                    cursor={{ fill: "rgba(249,115,22,0.05)" }}
                    formatter={(val) => [`${val} shop${val !== 1 ? "s" : ""}`, "Signups"]}
                  />
                  <Bar dataKey="shops" radius={[6, 6, 0, 0]}>
                    {chartData.map((_, i) => (
                      <Cell
                        key={i}
                        fill={i === chartData.length - 1 ? "#f97316" : "#334155"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>


          {/* Churn Risk Panel */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-white font-bold text-base mb-1" style={{ fontFamily: "'Sora', sans-serif" }}>
              ⚠️ Churn Risk
            </h2>
            <p className="text-slate-500 text-xs mb-5">Active shops with no orders in the last 7 days.</p>


            {churnRisk.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-slate-500 text-sm gap-2">
                <span className="text-3xl">🎉</span>
                All active shops placed orders recently.
              </div>
            ) : (
              <div className="space-y-3 max-h-52 overflow-y-auto pr-1">
                {churnRisk.map((shop) => (
                  <div key={shop.id} className="flex items-center justify-between bg-slate-800/60 rounded-xl px-4 py-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{shop.name}</p>
                      <p className="text-slate-400 text-xs">{shop.orders_count} total orders</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => openWhatsApp(shop.owner?.phone)}
                        className="bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/20 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors"
                      >
                        💬
                      </button>
                      <button
                        onClick={() => openCall(shop.owner?.phone)}
                        className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors"
                      >
                        📞
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


      </div>
    </SuperAdminLayout>
  );
}